import type { ComponentType } from "react";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PaymentsIcon from "@mui/icons-material/Payments";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SavingsIcon from "@mui/icons-material/Savings";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import GridViewIcon from "@mui/icons-material/GridView";
import { extractAccountFromStatementFilename } from "../../lib/statement-filename";

/**
 * Origin buckets for the Match Review filter (v3.0.70).
 *
 * Each bucket represents one type of "source document" a transaction
 * could have come from. Inferred from the trailing 4-digit account
 * suffix in the sourceFile.fileName (per Altelix Financial Extraction
 * Rules §"Account Identification"):
 *
 *   0517  → Credit Card    (2200 BOA CC #0517)
 *   1003  → Amex 1003      (1070 Ameri Express #1003)
 *   9167  → Operating      (1030 BOA - Oper)
 *   9170  → Payroll        (1050 BOA - Payroll)
 *   9183  → Wire           (1020 BOA - Wire)
 *   9196  → Merchant       (1040 BOA - Merchant)
 *   7175  → Checking       (1060 BOA - Checking)
 *   other → Other          (the row will surface in 'other' only)
 *
 * The Match Review filter exposes these as friendly labels so Michelle
 * can scope review to a specific document type (e.g. "show me only
 * credit-card match candidates").
 */

export type OriginBucketKey =
  | "all"
  | "credit_card"
  | "amex"
  | "operating"
  | "payroll"
  | "wire"
  | "merchant"
  | "checking"
  | "other";

export interface OriginBucketDef {
  key: OriginBucketKey;
  label: string;
  Icon: ComponentType<{ fontSize?: "small" | "medium" | "large" }>;
  color: string;
}

export const ORIGIN_BUCKETS: Record<OriginBucketKey, OriginBucketDef> = {
  all:         { key: "all",         label: "All Origins",          Icon: GridViewIcon,       color: "#6c5ce7" },
  credit_card: { key: "credit_card", label: "Credit Card Statement", Icon: CreditCardIcon,     color: "#fd79a8" },
  amex:        { key: "amex",        label: "Amex 1003",             Icon: CreditCardIcon,     color: "#0984e3" },
  operating:   { key: "operating",   label: "Bank — Operating",      Icon: AccountBalanceIcon, color: "#74b9ff" },
  payroll:     { key: "payroll",     label: "Bank — Payroll",        Icon: PaymentsIcon,       color: "#55efc4" },
  wire:        { key: "wire",        label: "Bank — Wire",           Icon: SwapHorizIcon,      color: "#fdcb6e" },
  merchant:    { key: "merchant",    label: "Bank — Merchant",       Icon: StorefrontIcon,     color: "#ffeaa7" },
  checking:    { key: "checking",    label: "Bank — Checking",       Icon: SavingsIcon,        color: "#a29bfe" },
  other:       { key: "other",       label: "Other / Unknown",       Icon: HelpOutlineIcon,    color: "#b2bec3" },
};

export const ORIGIN_BUCKET_ORDER: OriginBucketKey[] = [
  "all",
  "credit_card",
  "amex",
  "operating",
  "payroll",
  "wire",
  "merchant",
  "checking",
  "other",
];

const SUFFIX_TO_BUCKET: Record<string, OriginBucketKey> = {
  "0517": "credit_card",
  "1003": "amex",
  "9167": "operating",
  "9170": "payroll",
  "9183": "wire",
  "9196": "merchant",
  "7175": "checking",
};

/**
 * Extract the account suffix from a SharePoint filename.
 *
 * Delegates to the canonical resolver, which is dependency-free precisely so
 * the frontend bundle can share it instead of keeping a copy that drifts (this
 * copy had missed the account-vs-period fix, so a "9167 0626.pdf" row was
 * bucketed by its reporting period).
 */
function extractLast4(fileName: string | null | undefined): string | null {
  return extractAccountFromStatementFilename(fileName);
}

/**
 * Resolve the origin bucket for a match row. Inputs:
 *
 *   sourceFileName  — raw filename hydrated by `matches.listPendingReviewEnriched`
 *   sourceFileCategory — "bank_statement" | "invoice" | etc. (fallback)
 *
 * Returns the bucket key; "other" is the safe fallback when the
 * filename pattern doesn't match any known account suffix.
 */
export function originBucketFor(
  sourceFileName: string | null | undefined,
  sourceFileCategory: string | null | undefined,
): OriginBucketKey {
  const last4 = extractLast4(sourceFileName);
  if (last4 && SUFFIX_TO_BUCKET[last4]) return SUFFIX_TO_BUCKET[last4];
  if (sourceFileCategory === "invoice") return "other";
  return "other";
}
